import React, { useState } from 'react';
import { Functions, httpsCallable } from 'firebase/functions';
import './StoreDetails.css';

interface Store {
  storeId?: string;
  name: string;
  description?: string;
  address?: string;
  city: string;
  state?: string;
  zipCode?: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;
  timezone?: string;
  currency?: string;
  appId: string;
  orgId: string;
  stripeAccount: string;
  // Test environment Stripe details
  stripeTestAccountId?: string;
  stripeTestAccountName?: string;
  stripeTestConnected: boolean;
  stripeTestConnectUrl?: string;
  stripeTestConnectUrlExpires?: Date | any;
  stripeTestOnboardingStarted?: Date | any;
  stripeTestOnboardingCompletedAt?: Date | any;
  stripeTestConnectedAt?: Date | any;
  // Live environment Stripe details
  stripeLiveAccountId?: string;
  stripeLiveAccountName?: string;
  stripeLiveConnected: boolean;
  stripeLiveConnectUrl?: string;
  stripeLiveConnectUrlExpires?: Date | any;
  stripeLiveOnboardingStarted?: Date | any;
  stripeLiveOnboardingCompletedAt?: Date | any;
  stripeLiveConnectedAt?: Date | any;
  // Legacy fields for backward compatibility
  stripeAccountId?: string;
  stripeAccountName?: string;
  stripeConnected: boolean;
  stripeConnectUrl?: string;
  stripeConnectUrlExpires?: Date | any;
  stripeOnboardingStarted?: Date | any;
  isActive: boolean;
  createdAt: Date | any;
  updatedAt: Date | any;
}

interface StoreDetailsProps {
  store: Store;
  functions: Functions;
  onBack: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onStoreUpdated: () => void;
}

const StoreDetails: React.FC<StoreDetailsProps> = ({
  store,
  functions,
  onBack,
  onError,
  onSuccess,
  setLoading,
  onStoreUpdated
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'stripe'>('details');

  // Cloud function references
  const createStripeConnectAccountFunc = httpsCallable(functions, 'createStripeConnectAccount');
  const connectExistingStripeAccountFunc = httpsCallable(functions, 'connectExistingStripeAccount');
  const regenerateStripeConnectLinkFunc = httpsCallable(functions, 'regenerateStripeConnectLink');

  const handleCreateStripeAccount = async (environment: 'test' | 'live') => {
    setLoading(true);
    try {
      const result = await createStripeConnectAccountFunc({
        appId: store.appId,
        storeId: store.storeId,
        accountName: `${store.name} - ${environment.toUpperCase()}`,
        environment,
        returnUrl: `${window.location.origin}/stripe-return`,
        refreshUrl: `${window.location.origin}/stripe-refresh`
      });

      interface StripeConnectResponse {
        success: boolean;
        connectUrl: string;
        message: string;
      }

      const responseData = result.data as StripeConnectResponse;

      if (responseData && responseData.success) {
        // Open Stripe Connect in new window
        window.open(responseData.connectUrl, '_blank', 'width=600,height=700');
        onSuccess(`${environment.toUpperCase()} Stripe Connect link created. Complete setup in the new window.`);
        onStoreUpdated();
      } else {
        onError('Failed to create Stripe Connect account.');
      }
    } catch (err: any) {
      console.error('Error creating Stripe Connect account:', err);
      onError(err.message || 'Failed to create Stripe Connect account.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateLink = async (environment: 'test' | 'live') => {
    setLoading(true);
    try {
      const result = await regenerateStripeConnectLinkFunc({
        storeId: store.storeId,
        environment,
        returnUrl: `${window.location.origin}/stripe-return`,
        refreshUrl: `${window.location.origin}/stripe-refresh`
      });

      interface RegenerateLinkResponse {
        success: boolean;
        connectUrl: string;
        message: string;
      }

      const responseData = result.data as RegenerateLinkResponse;

      if (responseData && responseData.success) {
        // Open Stripe Connect in new window
        window.open(responseData.connectUrl, '_blank', 'width=600,height=700');
        onSuccess(`${environment.toUpperCase()} Stripe Connect link regenerated. Complete setup in the new window.`);
        onStoreUpdated();
      } else {
        onError('Failed to regenerate Stripe Connect link.');
      }
    } catch (err: any) {
      console.error('Error regenerating Stripe Connect link:', err);
      onError(err.message || 'Failed to regenerate Stripe Connect link.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | any): string => {
    if (!date) return 'Not available';
    
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      return dateObj.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStripeStatus = (environment: 'test' | 'live') => {
    if (environment === 'test') {
      return {
        connected: store.stripeTestConnected,
        accountId: store.stripeTestAccountId,
        accountName: store.stripeTestAccountName,
        connectUrl: store.stripeTestConnectUrl,
        onboardingStarted: store.stripeTestOnboardingStarted,
        connectedAt: store.stripeTestConnectedAt
      };
    } else {
      return {
        connected: store.stripeLiveConnected,
        accountId: store.stripeLiveAccountId,
        accountName: store.stripeLiveAccountName,
        connectUrl: store.stripeLiveConnectUrl,
        onboardingStarted: store.stripeLiveOnboardingStarted,
        connectedAt: store.stripeLiveConnectedAt
      };
    }
  };

  const renderStripeSection = (environment: 'test' | 'live') => {
    const status = getStripeStatus(environment);
    const isConnected = status.connected;
    const hasAccountId = !!status.accountId;
    const hasConnectUrl = !!status.connectUrl;

    return (
      <div className={`stripe-section ${environment}`}>
        <div className="stripe-section-header">
          <h4>{environment.toUpperCase()} Environment</h4>
          <span className={`stripe-status-badge ${isConnected ? 'connected' : 'not-connected'}`}>
            {isConnected ? '✓ Connected' : 'Not Connected'}
          </span>
        </div>

        <div className="stripe-section-content">
          {isConnected ? (
            <div className="stripe-connected-info">
              <div className="info-row">
                <label>Account ID:</label>
                <span className="monospace">{status.accountId}</span>
              </div>
              <div className="info-row">
                <label>Account Name:</label>
                <span>{status.accountName}</span>
              </div>
              <div className="info-row">
                <label>Connected At:</label>
                <span>{formatDate(status.connectedAt)}</span>
              </div>
              <div className="stripe-actions">
                <button
                  className="stripe-action-btn secondary"
                  onClick={() => onSuccess(`Manage ${environment.toUpperCase()} Stripe account coming soon!`)}
                >
                  Manage Account
                </button>
              </div>
            </div>
          ) : hasAccountId ? (
            <div className="stripe-pending-info">
              <div className="info-row">
                <label>Account ID:</label>
                <span className="monospace">{status.accountId}</span>
              </div>
              <div className="info-row">
                <label>Status:</label>
                <span className="status-pending">Onboarding in progress</span>
              </div>
              <div className="info-row">
                <label>Started:</label>
                <span>{formatDate(status.onboardingStarted)}</span>
              </div>
              <div className="stripe-actions">
                {hasConnectUrl ? (
                  <button
                    className="stripe-action-btn primary"
                    onClick={() => window.open(status.connectUrl, '_blank', 'width=600,height=700')}
                  >
                    Continue Setup
                  </button>
                ) : (
                  <button
                    className="stripe-action-btn primary"
                    onClick={() => handleRegenerateLink(environment)}
                  >
                    Regenerate Link
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="stripe-not-connected">
              <p>No {environment} Stripe account connected yet.</p>
              <div className="stripe-actions">
                <button
                  className="stripe-action-btn primary"
                  onClick={() => handleCreateStripeAccount(environment)}
                >
                  Create {environment.toUpperCase()} Account
                </button>
                <button
                  className="stripe-action-btn secondary"
                  onClick={() => onSuccess(`Connect existing ${environment} account coming soon!`)}
                >
                  Connect Existing
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="store-details">
      {/* Header */}
      <div className="store-details-header">
        <button className="store-details-back-btn" onClick={onBack}>
          ← Back to Store Management
        </button>
        <h1>{store.name}</h1>
        <span className={`store-status-badge ${store.isActive ? 'active' : 'inactive'}`}>
          {store.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Tabs */}
      <div className="store-details-tabs">
        <button
          className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Store Details
        </button>
        <button
          className={`tab-btn ${activeTab === 'stripe' ? 'active' : ''}`}
          onClick={() => setActiveTab('stripe')}
        >
          Stripe Integration
        </button>
      </div>

      {/* Content */}
      <div className="store-details-content">
        {activeTab === 'details' && (
          <div className="details-tab">
            <div className="details-grid">
              <div className="details-section">
                <h3>Basic Information</h3>
                <div className="details-row">
                  <label>Store ID:</label>
                  <span className="monospace">{store.storeId}</span>
                </div>
                <div className="details-row">
                  <label>Name:</label>
                  <span>{store.name}</span>
                </div>
                <div className="details-row">
                  <label>Description:</label>
                  <span>{store.description || 'Not provided'}</span>
                </div>
              </div>

              <div className="details-section">
                <h3>Location</h3>
                <div className="details-row">
                  <label>Address:</label>
                  <span>{store.address || 'Not provided'}</span>
                </div>
                <div className="details-row">
                  <label>City:</label>
                  <span>{store.city}</span>
                </div>
                <div className="details-row">
                  <label>State/Province:</label>
                  <span>{store.state || 'Not provided'}</span>
                </div>
                <div className="details-row">
                  <label>Zip/Postal Code:</label>
                  <span>{store.zipCode || 'Not provided'}</span>
                </div>
                <div className="details-row">
                  <label>Country:</label>
                  <span>{store.country}</span>
                </div>
              </div>

              <div className="details-section">
                <h3>Contact Information</h3>
                <div className="details-row">
                  <label>Phone:</label>
                  <span>{store.phone || 'Not provided'}</span>
                </div>
                <div className="details-row">
                  <label>Email:</label>
                  <span>{store.email || 'Not provided'}</span>
                </div>
                <div className="details-row">
                  <label>Website:</label>
                  <span>
                    {store.website ? (
                      <a href={store.website} target="_blank" rel="noopener noreferrer">
                        {store.website}
                      </a>
                    ) : (
                      'Not provided'
                    )}
                  </span>
                </div>
              </div>

              <div className="details-section">
                <h3>Business Settings</h3>
                <div className="details-row">
                  <label>Timezone:</label>
                  <span>{store.timezone || 'Not set'}</span>
                </div>
                <div className="details-row">
                  <label>Currency:</label>
                  <span>{store.currency || 'Not set'}</span>
                </div>
                <div className="details-row">
                  <label>Organization:</label>
                  <span>{store.orgId}</span>
                </div>
                <div className="details-row">
                  <label>Stripe Account:</label>
                  <span>{store.stripeAccount}</span>
                </div>
              </div>

              <div className="details-section">
                <h3>System Information</h3>
                <div className="details-row">
                  <label>App ID:</label>
                  <span className="monospace">{store.appId}</span>
                </div>
                <div className="details-row">
                  <label>Created:</label>
                  <span>{formatDate(store.createdAt)}</span>
                </div>
                <div className="details-row">
                  <label>Updated:</label>
                  <span>{formatDate(store.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stripe' && (
          <div className="stripe-tab">
            <div className="stripe-tab-header">
              <h3>Stripe Payment Integration</h3>
              <p>Connect your store to Stripe for payment processing. You need both test and live environments.</p>
            </div>

            <div className="stripe-environments">
              {renderStripeSection('test')}
              {renderStripeSection('live')}
            </div>

            <div className="stripe-info-box">
              <div className="info-icon">ℹ️</div>
              <div>
                <h4>About Stripe Integration</h4>
                <ul>
                  <li><strong>Test Environment:</strong> Use for testing payments with fake card numbers</li>
                  <li><strong>Live Environment:</strong> Required for real payments and store activation</li>
                  <li><strong>Store Status:</strong> Your store is only active when live Stripe is connected</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreDetails;